import { createAsyncThunk } from "@reduxjs/toolkit";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { optimizeImage } from "../../utils/optimizeImage";
import axiosProtected from "../config/baseWithToken";
import { getFulfillmentOrders } from "../../redux/slices/orderSlice";
import { axiosClient } from "../config/base";
import { isClientPreview } from "../../utils/clientPreview";
import SyncToast from "../../components/loader/SyncToast";

export const updateOrder = createAsyncThunk(
  "store/orders/update",
  async (orderInfo, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.put(
        `/store/orders/${orderInfo.orderId}`,
        orderInfo.updateDetails
      );
      dispatch(getOrders({ page: orderInfo?.page || 0, size: 50, status:orderInfo?.status }));
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);
export const updateOrderStatus = createAsyncThunk(
  "store/orders/update/status",
  async (orderInfo, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.put(
        `/store/orders/status/${orderInfo.orderId}`,
        orderInfo.updateDetails
      );
      dispatch(getOrders({ page: 0, size: 50 }));
      // dispatch(getProcessingOrders({ page: 0, limit: 50 }));
      // dispatch(getWaitingOrders({ page: 0, limit: 50 }));
      // dispatch(getShippedOrders({ page: 0, limit: 50 }));
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const shippingOrder = createAsyncThunk(
  "store/orders/ship",
  async (orderInfo, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.put(
        `/admin/orders/shipped/${orderInfo.orderId}`,
        orderInfo.updateDetails
      );
      dispatch(getAllOrders({ page: 0, limit: 50 }));
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const processMultipleOrders = createAsyncThunk(
  "store/orders/batch-process",
  async (importedOrders, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.put(
        `/admin/orders/batch-process`,
        importedOrders
      );
      dispatch(getAllBatchProcessedOrders({ page: 0, size: 50 }));
      dispatch(getAllOrders({ page: 0, limit: 50 }));
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const getAllBatchProcessedOrders = createAsyncThunk(
  "admin/orders/batch-process/get",
  async (batchProcess, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.get(
        `/admin/orders/batch-process?page=${batchProcess.page}&size=${batchProcess.size}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const deleteBatchProcess = createAsyncThunk(
  "admin/orders/batch-process/delete",
  async (processId, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.put(
        `/admin/orders/batch-process/delete/${processId}`
      );
      dispatch(getAllBatchProcessedOrders({ page: 0, limit: 50 }));
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const retryBatchProcess = createAsyncThunk(
  "admin/orders/batch-process/retry",
  async (batchProcess, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.put(
        `/admin/orders/batch-process/${batchProcess.processId}`,
        batchProcess.updateDetails
      );
      dispatch(getAllBatchProcessedOrders({ page: 0, limit: 50 }));
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const deleteOrder = createAsyncThunk(
  "store/orders/delete",
  async (orderPayload, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.put(
        `/admin/orders/delete/${orderPayload?.orderId}`,
        orderPayload?.transactionPayload
      );
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);
export const deleteOrderCustomer = createAsyncThunk(
  "store/orders/delete/customer",
  async (orders, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.put(`/store/orders/delete`, orders);
      dispatch(getOrders({ page: 0, size: 50 }));
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const getOrders = createAsyncThunk(
  "store/orders",
  async (order, thunkAPI) => {
    let url = `/store/orders?page=${order.page || 0}&size=${order.size || 50}`;
    if (order.status) {
      url = url + `&status=${order.status}`;
    }
    if (order.text) {
      url = url + `&text=${order.text}`;
    }
    if (order.storeId) {
      url = url + `&storeId=${order.storeId}`;
    }
    try {
      let { data } = await axiosProtected.get(url);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

const SYNC_TOAST = "order-sync";

export const syncOrders = createAsyncThunk(
  "store/sync/orders",
  async (order, { dispatch, rejectWithValue }) => {
    let url = `/store/orders?sync=yes`;
    // Client preview: one toast per sync that turns from loading into done, so a second
    // click or a slow response never stacks a second success toast.
    const preview = !order && isClientPreview();
    if (preview) {
      const loading = <SyncToast state="loading" />;
      if (toast.isActive(SYNC_TOAST)) toast.update(SYNC_TOAST, { render: loading, autoClose: false, icon: false, hideProgressBar: true });
      else toast(loading, { toastId: SYNC_TOAST, autoClose: false, closeButton: false, icon: false, hideProgressBar: true });
    }
    try {
      let { data } = await axiosProtected.get(url);
      dispatch(getOrders({ page: 0, size: 50 }));
      if (preview) {
        toast.update(SYNC_TOAST, { render: <SyncToast state="done" />, autoClose: 5000, closeButton: true, icon: false, hideProgressBar: true });
      } else if (!order) {
        toast.success("Order synchronization successful");
      }
      return data;
    } catch (e) {
      if (preview) toast.dismiss(SYNC_TOAST);
      toast.error(e?.response?.data?.message || "Order synchronization failed");
      rejectWithValue(e?.response?.data);
    }
  }
);

export const getOrderAnalytics = createAsyncThunk(
  "store/orders/analytics",
  async (days, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/store/orders/analytics?days=${days}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const getAllOrders = createAsyncThunk(
  "admin/orders",
  async (order, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/admin/orders?page=${order.page || 0}&storeId=${
          order?.storeId || ""
        }&status=${order?.status || ""}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);
export const getTotalOrders = createAsyncThunk(
  "admin/orders/totals",
  async (order, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/admin/orders/totals`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const calculationForRefund = createAsyncThunk(
  "admin/orders/calculation-for-refund",
  async (lineItems, thunkAPI) => {
    try {
      let { data } = await axiosProtected.post(
        `/admin/orders/calculation-for-refund`,
        lineItems
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const refundOrderAmount = createAsyncThunk(
  "admin/orders/refund",
  async (payload, thunkAPI) => {
    try {
      let { data } = await axiosProtected.put(
        `/admin/orders/refund/${payload?.orderId}`,
        payload?.lineItems
      );
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const searchOrders = createAsyncThunk(
  "store/orders/search",
  async (order, thunkAPI) => {
    try {
      let url = `/store/orders/search?text=${order?.text || ""}`
      if(order.page){
        url = url + `&page=${order.page}`
      }
        let { data } = await axiosProtected.get(url);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const processOrder = createAsyncThunk(
  "store/orders/process",
  async (order, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.post(`/store/orders/process`, order);
      toast.success(data.message);
      dispatch(getOrders({ page: 0, limit: 50 }));
      // dispatch(getWaitingOrders({ page: 0, limit: 50 }));
      // dispatch(getProcessingOrders({ page: 0, size: 50 }));
      dispatch(getFulfillmentOrders([]));
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const readyToFulfillOrders = createAsyncThunk(
  "store/orders/process",
  async (order, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.post(
        `/store/orders/calculation`,
        order
      );
      // toast.success(data.message);
      dispatch(getFulfillmentOrders(data));
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const calculateCostsOfOrders = createAsyncThunk(
  "/store/orders/calculate-costs",
  async (orders, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.post(
        `/store/orders/calculate-costs`,
        orders
      );
      return data?.data?.totals;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const getDateFilterOrders = createAsyncThunk(
  "store/date-filter-orders",
  async (dates, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/store/orders/date?start_date=${dates.start_date}&end_date=${dates.end_date}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);
export const getWaitingOrders = createAsyncThunk(
  "store/orders/waiting",
  async (order, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/store/orders/waiting?page=${order.page}&size=${order.size}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);
export const getProcessingOrders = createAsyncThunk(
  "store/orders/processing",
  async (order, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/store/orders/processing?page=${order.page}&size=${50}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);
export const getShippedOrders = createAsyncThunk(
  "store/orders/shipped",
  async (order, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/store/orders/shipped?page=${order.page}&size=${50}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const getDisputeList = createAsyncThunk(
  "store/dispute-list",
  async (dispute, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/store/dispute-list?page=${dispute.page}&size=${50}&text=${
          dispute.text || ""
        }&status=${dispute.status || ""}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const getAllDisputes = createAsyncThunk(
  "admin/orders/disputes",
  async (dispute, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/admin/orders/disputes?page=${dispute.page}&size=${50}&text=${
          dispute.text || ""
        }&status=${dispute.status || ""}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);
export const createdDisputesNumber = createAsyncThunk(
  "store/dispute-list",
  async (orderId, thunkAPI) => {
    try {
      const { data } = await axiosProtected.get(
        `/store/dispute-list/count/${orderId}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);
export const manipulateReturnedOrders = createAsyncThunk(
  "admin/orders/returned-orders-manipulation",
  async (orderPayload, thunkAPI) => {
    try {
      const { data } = await axiosProtected.post(
        `/admin/orders/returned-orders-manipulation`,
        orderPayload
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const createReturnedOverseasOrder = createAsyncThunk(
  "store/orders/returned-overseas",
  async (orderPayload, thunkAPI) => {
    try {
      const { data } = await axiosProtected.post(
        `/store/orders/returned-overseas`,
        orderPayload
      );
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const getReturnedOrders = createAsyncThunk(
  "store/orders/returned-overseas/get",
  async (payload, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/store/orders/returned-overseas?status=${payload?.status || ""}&page=${
          payload?.page || 0
        }&searchTerm=${payload?.searchTerm || ""}`
      );
      return data?.data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const updateReturnedOrder = createAsyncThunk(
  "/store/orders/returned-overseas/update",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.put(
        `/store/orders/returned-overseas/${payload.orderId}`,
        payload.updateDetails
      );
      dispatch(getReturnedOrders());
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const getDateFilterDisputes = createAsyncThunk(
  "store/date-filter-disputes",
  async (dates, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/store/dispute-list/date?start_date=${dates.start_date}&end_date=${dates.end_date}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const getPendingDisputeList = createAsyncThunk(
  "store/dispute-list/pending",
  async (dispute, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/store/dispute-list/pending?page=${dispute.page}&size=${50}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const getDeliveredDisputeList = createAsyncThunk(
  "store/dispute-list/delivered",
  async (dispute, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/store/dispute-list/delivered?page=${dispute.page}&size=${50}`
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const getAllMails = createAsyncThunk(
  "admin/mail-trackers",
  async (dispute, thunkAPI) => {
    try {
      let { data } = await axiosProtected.get(
        `/admin/mail-trackers?page=${dispute.page}&size=${50}&status=${
          dispute?.status || ""
        }`
      );
      return data?.data;
    } catch (e) {
      toast.error(e.response.data.message);
      thunkAPI.rejectWithValue(e.response.data);
    }
  }
);

export const postDisputeModalData = createAsyncThunk(
  "store/dispute-list/post",
  async (disputeData, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.post(
        `/store/dispute-list`,
        disputeData
      );
      toast.success(data.message);
      dispatch(getDisputeList({ page: 0, size: 50 }));
      // dispatch(getPendingDisputeList({ page: 0, size: 50 }));
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const archiveDispute = createAsyncThunk(
  "customer/orders/disputes/archive",
  async (disputeInfo, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.put(
        `/store/dispute-list/${disputeInfo.disputeId}`,
        disputeInfo.updateDetails
      );
      dispatch(getDisputeList({ page: 0, limit: 50 }));
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const uploadFile = createAsyncThunk(
  "store/upload",
  async (uploadData, { dispatch, rejectWithValue }) => {
    try {
      // The server takes about 1 MB per request; shrink big photos first.
      const file = uploadData?.get?.("media");
      if (file?.type?.startsWith("image/") && file.size > 900 * 1024) {
        try {
          uploadData.set("media", await optimizeImage(file, { maxSide: 2000 }));
        } catch (e) {
          // Unreadable image: try the original.
        }
      }
      let { data } = await axiosProtected.post(`/store/upload`, uploadData);
      return data;
    } catch (e) {
      // A file the server refuses (often too large) comes back without a body.
      const message =
        e?.response?.data?.message ||
        (e?.response ? "The file could not be uploaded. Try a smaller image or a screenshot." : "No connection to the server. Try again.");
      toast.error(message);
      return rejectWithValue({ ...(e?.response?.data || {}), message });
    }
  }
);

export const stripePayment = createAsyncThunk(
  "store/orders/payment-checkout",
  async (orderInfo, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.post(
        `/store/orders/payment-checkout`,
        orderInfo
      );
      if (data.url) {
        window.location.href = data.url;
      }
      // dispatch(getAllOrders({ page: 0, limit: 50 }));
      toast.success(data.message);
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const createPaypalOrder = createAsyncThunk(
  "store/orders/create-paypal-order",
  async (orderInfo, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosClient.post(
        `/store/orders/create-paypal-order`,
        JSON.stringify({
          cart: orderInfo,
        })
      );
      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);

export const capturePaypalPayment = createAsyncThunk(
  "store/orders/capture-paypal-order",
  async (orderInfo, { dispatch, rejectWithValue }) => {
    try {
      let { data } = await axiosProtected.post(
        `/store/orders/capture-paypal-order`,
        { orderId: orderInfo }
      );

      return data;
    } catch (e) {
      toast.error(e.response.data.message);
      rejectWithValue(e.response.data);
    }
  }
);
